import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { fetchPatients } from "../../core/redux/slices/patientSlice";
import { fetchInvoices } from "../../core/redux/slices/invoiceSlice";
import { getInvoice } from "../../core/api/invoiceService";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => (v ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "-");
const sameDate = (iso, date) => !!iso && iso.split("T")[0] === date;

const DailyReport = () => {
  const dispatch = useDispatch();
  const patients = useSelector((s) => s.patients.list);
  const invoices = useSelector((s) => s.invoices.list);

  const [date, setDate] = useState(today());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchPatients());
    dispatch(fetchInvoices());
  }, [dispatch]);

  const patientById = useMemo(() => {
    const m = new Map();
    patients.forEach((p) => m.set(p.patientID || p.id, p));
    return m;
  }, [patients]);

  const runReport = async () => {
    setLoading(true);
    try {
      // Payments collected on `date` may be recorded against an invoice created on a
      // different (earlier) day. There's no server-side "payments in date range" endpoint
      // (getDailyCollection exists but is unwired/unconfirmed), so we bound the invoices
      // we need full payment detail for to: invoices dated `date` itself, plus any invoice
      // that's still outstanding (only those can receive a payment after their invoice date).
      const candidates = invoices.filter((inv) => sameDate(inv.invoiceDate, date) || Number(inv.balanceAmount || 0) > 0);
      const details = await Promise.all(candidates.map((inv) => getInvoice(inv.id).then((r) => r.data).catch(() => null)));

      const out = [];
      details.filter(Boolean).forEach((inv) => {
        const dayPayments = (inv.payments || []).filter((p) => sameDate(p.paymentDate, date));
        if (!dayPayments.length) return;
        const patient = patientById.get(inv.patientID ?? inv.patientId);
        const treatmentAmount = (inv.procedureLines || []).reduce((s, l) => s + Number(l.amount || 0), 0);
        const medicine = (inv.productLines || []).reduce((s, l) => s + Number(l.amount || 0), 0);
        const total = treatmentAmount + medicine;
        const modeTotal = (mode) => dayPayments.filter((p) => p.paymentMode === mode).reduce((s, p) => s + Number(p.amount || 0), 0);
        const cash = modeTotal("Cash");
        const swipe = modeTotal("Card");
        const gpay = modeTotal("UPI");
        const other = dayPayments
          .filter((p) => !["Cash", "Card", "UPI"].includes(p.paymentMode))
          .reduce((s, p) => s + Number(p.amount || 0), 0);
        out.push({
          invoiceId: inv.invoiceID ?? inv.id,
          patientFileNo: patient?.opNumber || "—",
          patientName: inv.patientName || patient?.patientName || "—",
          ageGender: patient ? `${patient.age ?? "—"}/${patient.gender || "—"}` : "—",
          mobile: patient?.mobileNumber || "—",
          address: patient?.address || "—",
          treatmentAmount, medicine, total,
          cash, swipe, gpay, other,
          remarks: inv.remarks || "",
        });
      });
      setRows(out);
    } catch {
      Swal.fire("Error", "Failed to build the daily report.", "error");
    } finally {
      setLoading(false);
    }
  };

  const totals = (rows || []).reduce((t, r) => ({
    treatmentAmount: t.treatmentAmount + r.treatmentAmount,
    medicine: t.medicine + r.medicine,
    total: t.total + r.total,
    cash: t.cash + r.cash,
    swipe: t.swipe + r.swipe,
    gpay: t.gpay + r.gpay,
    other: t.other + r.other,
  }), { treatmentAmount: 0, medicine: 0, total: 0, cash: 0, swipe: 0, gpay: 0, other: 0 });

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Daily Report</h4><h6>Patient-wise daily cash flow / collection</h6></div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-1">Date</label>
                <input type="date" className="form-control form-control-sm" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={runReport} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {rows && (
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>S.No</th>
                      <th>Patient ID</th>
                      <th>Patient Name</th>
                      <th>Age/Gender</th>
                      <th>Mobile No</th>
                      <th>Address</th>
                      <th className="text-end">Treatment Amount</th>
                      <th className="text-end">Medicine Amount</th>
                      <th className="text-end">Total</th>
                      <th className="text-end">Cash</th>
                      <th className="text-end">Swipe</th>
                      <th className="text-end">GPay</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={13} className="text-center text-muted py-3">No collections recorded for this date.</td></tr>
                    ) : rows.map((r, idx) => (
                      <tr key={r.invoiceId}>
                        <td>{idx + 1}</td>
                        <td>{r.patientFileNo}</td>
                        <td>{r.patientName}</td>
                        <td>{r.ageGender}</td>
                        <td>{r.mobile}</td>
                        <td>{r.address}</td>
                        <td className="text-end">{fmt(r.treatmentAmount)}</td>
                        <td className="text-end">{fmt(r.medicine)}</td>
                        <td className="text-end fw-semibold">{fmt(r.total)}</td>
                        <td className="text-end">{fmt(r.cash)}</td>
                        <td className="text-end">{fmt(r.swipe + r.other)}</td>
                        <td className="text-end">{fmt(r.gpay)}</td>
                        <td>{r.remarks || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  {rows.length > 0 && (
                    <tfoot>
                      <tr className="table-light fw-bold">
                        <td colSpan={6} className="text-end">TOTAL PATIENT RECEIPTS</td>
                        <td className="text-end">{fmt(totals.treatmentAmount)}</td>
                        <td className="text-end">{fmt(totals.medicine)}</td>
                        <td className="text-end">{fmt(totals.total)}</td>
                        <td className="text-end">{fmt(totals.cash)}</td>
                        <td className="text-end">{fmt(totals.swipe + totals.other)}</td>
                        <td className="text-end">{fmt(totals.gpay)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        )}

        {!rows && !loading && (
          <div className="card text-center py-5 text-muted">
            <p>Select a date and click <strong>Search</strong> to view the daily report.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyReport;
